import { SystemIntegrationsReadmodel } from '@bett3r-dev/teselly-domain';
/**
 * Invoicing State
 *
 * Contains all state for the Invoicing subdomain.
 * Copied from the ERPs subdomain and adapted: an invoicing integration is a
 * provisioned `SystemIntegration` (the same canonical readmodel shape used by
 * channels/erps), so we reuse `SystemIntegrationsReadmodel` rather than
 * inventing a parallel record type.
 *
 * Credential form state is intentionally NOT in CDSE — it lives locally in
 * `AddInvoicingModal` (React `useState`), per the established channels/erps
 * convention.
 */

/**
 * A provisioned invoicing integration is a `SystemIntegration` (with the
 * `issues-invoices` capability), mirroring how channels/erps type their
 * provisioned-integration record.
 */
export type InvoicingIntegration = SystemIntegrationsReadmodel;

export interface InvoicingState {
  /** Provisioned invoicing integrations, keyed by integration id */
  data: {
    integrations: Record<string, InvoicingIntegration>;
  };

  /** UI state — add modal + selected connector */
  ui: {
    addModalOpen: boolean;
    selectedConnectorId: string | null;
  };

  /** Loading states */
  loading: {
    listLoading: boolean;
    submitting: boolean;
  };

  /**
   * Transient in-flight submission payload. Populated by `submit/start` so the
   * submit policy (which only receives state in `onStateChange`) can read the
   * connector + credential form values to provision. Cleared on
   * success/error. NOT persisted credential form state — that still lives
   * locally in `AddInvoicingModal`; this is only the snapshot handed to the
   * policy at submit time.
   */
  pendingSubmission: {
    connectorId: string;
    credentials: Record<string, string | undefined>;
    /**
     * Display name for a `directProvision` instance, entered in the modal and
     * forwarded by the submit policy as the integration `label` so multiple
     * instances of the same connector are distinguishable in the list. Unused by
     * the `apiKey` path.
     */
    label?: string;
  } | null;

  /**
   * Transient in-flight deletion marker. Populated by `delete/start` so the
   * delete policy can identify which integration to remove and distinguish a
   * deletion from a submission (both share `loading.submitting`). Cleared on
   * success/error.
   */
  pendingDeletion: {
    integrationId: string;
  } | null;
}

export const initialState: InvoicingState = {
  data: {
    integrations: {}
  },

  ui: {
    addModalOpen: false,
    selectedConnectorId: null
  },

  loading: {
    // Starts true so the list view shows its LoadingOverlay until the
    // list-load policy delivers the first `list/set` snapshot (which clears it).
    listLoading: true,
    submitting: false
  },

  pendingSubmission: null,
  pendingDeletion: null
};

export const selectors = {
  getInvoicingIntegration: ( state: InvoicingState, integrationId: string ) =>
    state.data.integrations[integrationId],

  selectInvoicingIntegrations: ( state: InvoicingState ) =>
    Object.values( state.data.integrations ),

  selectAddModalOpen: ( state: InvoicingState ) =>
    state.ui.addModalOpen,

  selectedConnectorId: ( state: InvoicingState ) =>
    state.ui.selectedConnectorId,

  isListLoading: ( state: InvoicingState ) =>
    state.loading.listLoading,

  isSubmitting: ( state: InvoicingState ) =>
    state.loading.submitting
};
